import cx from 'classnames'
import {
  ChangeEventHandler,
  type FormEventHandler,
  type HTMLProps,
  type ReactNode,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react'

import { Copy } from '@mondough/copy'
import Checkbox from '@mondough/monzo-ui/src/components/checkbox'
import ChevronDown from '@mondough/monzo-ui/src/components/icon/icons/chevron-down'

import {
  type ConsentCategory,
  categories as defaultCategories,
} from '../core/constants'
import { useConsentPreferences } from '../react'
import { Button } from './components/button'
import styles from './form.module.scss'

export type CookieConsentFormBaseProps = {
  onSubmit?: (allowedCategories: ConsentCategory[]) => void
  categories?: ConsentCategory[] | typeof defaultCategories
}

export type CookieConsentFormProps = CookieConsentFormBaseProps &
  Omit<HTMLProps<HTMLFormElement>, 'onSubmit'>

export function CookieConsentForm({
  className,
  onSubmit,
  categories = defaultCategories,
  ...formProps
}: CookieConsentFormProps) {
  const [isClient, setIsClient] = useReducer(() => true, false)
  useEffect(() => setIsClient(), [])

  const consents = useConsentPreferences()
  const [allowed, setAllowed] = useState(consents.allowed)

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    (event) => {
      event.preventDefault()
      consents.allowOnly(allowed)
      onSubmit?.([...allowed])
    },
    [allowed, consents, onSubmit],
  )

  const handleCategoryChange = useCallback<
    ChangeEventHandler<HTMLInputElement>
  >((event) => {
    setAllowed((prev) => {
      const next = new Set(prev)
      const category = event.target.name as ConsentCategory
      next[event.target.checked ? 'add' : 'delete'](category)
      return next
    })
  }, [])

  if (!isClient) return null

  return (
    <form
      {...formProps}
      className={cx(styles.form, className)}
      onSubmit={handleSubmit}
    >
      <div className={styles.categories}>
        {categories.map((category) => (
          <ConsentCategoryCheckbox
            key={category}
            category={category}
            checked={allowed.has(category)}
            onChange={handleCategoryChange}
          />
        ))}
      </div>
      <Button type="submit">Save preferences</Button>
    </form>
  )
}

const categoryLabels: Record<
  ConsentCategory,
  { label: ReactNode; summary: ReactNode }
> = {
  analytics: {
    label: (
      <Copy.Simple
        id="consent-management.category-analytics.label"
        fallback="Analytics cookies"
      />
    ),
    summary: (
      <Copy.Simple
        id="consent-management.category-analytics.summary"
        fallback="The science-y sounding cookie that’s interested in what you do on our website. Where you came from, what pages you visit and for how long."
      />
    ),
  },
  advertising: {
    label: (
      <Copy.Simple
        id="consent-management.category-advertising.label"
        fallback="Advertising cookies"
      />
    ),
    summary: (
      <Copy.Simple
        id="consent-management.category-advertising.summary"
        fallback="What’s worse than ads? Irrelevant ads. We follow your trail of virtual cookie crumbs to show you more interesting ads and marketing."
      />
    ),
  },
  marketing: {
    label: (
      <Copy.Simple
        id="consent-management.category-marketing.label"
        fallback="Marketing cookies"
      />
    ),
    summary: (
      <Copy.Simple
        id="consent-management.category-marketing.summary"
        fallback="The curious cookie that gets to know you better. By knowing your preferences on the site, we can enhance your journey to make things more relevant to you."
      />
    ),
  },
  required: {
    label: (
      <Copy.Simple
        id="consent-management.category-required.label"
        fallback="Required cookies"
      />
    ),
    summary: (
      <Copy.Simple
        id="consent-management.category-required.summary"
        fallback="This trusty cookie is a must for our website to work and keep you safe and secure. Need we say more?"
      />
    ),
  },
}

function ConsentCategoryCheckbox({
  category,
  checked,
  onChange,
}: {
  category: ConsentCategory
  checked: boolean
  onChange: ChangeEventHandler<HTMLInputElement>
}) {
  const isRequired = category === 'required'
  const copy = categoryLabels[category]

  return (
    <details>
      <summary>
        {/* TODO: remove dependency on monzo-ui <Checkbox /> */}
        <Checkbox
          key={category}
          id={`cookie-consent-option-${category}`}
          name={category}
          label={copy.label}
          checked={checked || isRequired}
          onChange={onChange}
          disabled={isRequired}
          marginTop="none"
        />
        <ChevronDown className={styles.chevron} />
      </summary>
      <p>{copy.summary}</p>
    </details>
  )
}
